/**
 * Tax calculation services — API calls, normalization, and request builders.
 * No React; receives cart/address as arguments.
 */

import { PRODUCTS, STORES, CATEGORIES } from '../../../shared/constants/api';
import * as api from '../../../shared/services/apiClient';
import {
  DEFAULT_CURRENCY,
  DEFAULT_PRODUCT_NAME,
  DEFAULT_TITLE_TRANSFER,
  DEFAULT_PRIMARY_LANGUAGE,
  TAX_ATTRIBUTES,
  TAX_ADDRESS_FIELDS,
  TAX_COUNTRIES,
} from '../constants/tax.constants';
import type { CartLineItem, Cart, AddressInput, TaxApiResponse, OneSourceLineItem, NormalizedTaxResult } from '../types/tax.types';
import { aggregateTaxLines } from '../utils/aggregateTaxLines';

// ─── API Call ─────────────────────────────────────────────────────────

export const callTaxApiDirect = async (taxBody: unknown): Promise<TaxApiResponse> => {
  const ping_access_token = localStorage.getItem('ping_access_token');
  const res = await fetch(
    import.meta.env.VITE_BACKEND_API_URL + '/api/tax/calculate',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ping_access_token}` },
      body: JSON.stringify(taxBody),
    }
  );
  if (!res.ok) throw new Error(`Tax API error: ${res.status} ${await res.text()}`);
  return res.json() as Promise<TaxApiResponse>;
};

// ─── Normalize ────────────────────────────────────────────────────────

export const normalizeTaxResult = (taxResult: TaxApiResponse, currentCart: Cart): NormalizedTaxResult => {
  const subtotal = (currentCart.totalPrice?.centAmount ?? 0) / 100;
  const discountAmount = (currentCart.totalDiscount ?? 0) / 100;
  const apiLineItems: OneSourceLineItem[] = taxResult?.data?.lineItems ?? [];
  const taxRate = apiLineItems[0]?.priceData?.taxRate ?? 0;
  const taxAmount = subtotal * taxRate;

  // Build lineReference → quantity map so aggregateTaxLines can multiply
  // authorityTaxAmount by the actual cart quantity for each line item.
  // lineReference in the OneSource response is 1-indexed (idx + 1).
  const quantityByLineRef: Record<number, number> = {};
  (currentCart.lineItems ?? []).forEach((item, idx) => {
    quantityByLineRef[idx + 1] = item.quantity ?? 1;
  });

  return {
    raw: taxResult,
    taxAmount,
    taxRate,
    grandTotal: subtotal + taxAmount,
    subtotal,
    discountAmount,
    lineItems: apiLineItems,
    aggregatedTaxLines: aggregateTaxLines(apiLineItems, quantityByLineRef),
  };
};

// ─── Data Fetchers ────────────────────────────────────────────────────

export const fetchProductDescriptions = async (cart: Cart): Promise<Record<string, string>> => {
  const ids = [...new Set((cart.lineItems ?? []).map((i) => i.productId))];
  if (!ids.length) return {};
  try {
    const res = await api.get(PRODUCTS, { where: `id in ("${ids.join('","')}")`, limit: '50' });
    if (!res.ok) throw new Error();
    const data = await res.json();
    const map: Record<string, string> = {};
    (data.results ?? []).forEach((p: { id: string; masterData?: { current?: { description?: Record<string, string> } } }) => {
      const desc = p.masterData?.current?.description;
      const val = desc?.['en'] || Object.values(desc ?? {})[0];
      if (val) map[p.id] = val;
    });
    return map;
  } catch (error) {
    console.error('[tax] Failed to fetch product descriptions:', error);
    return {};
  }
};

export const fetchCategories = async (): Promise<Record<string, unknown>> => {
  try {
    const res = await api.get(CATEGORIES, { limit: '500' });
    if (!res.ok) throw new Error();
    const data = await res.json();
    return Object.fromEntries((data.results ?? []).map((c: { id: string }) => [c.id, c]));
  } catch (error) {
    console.error('[tax] Failed to fetch categories:', error);
    return {};
  }
};

export const fetchStores = async (): Promise<Record<string, unknown>> => {
  try {
    const res = await api.get(STORES, { limit: '100' });
    if (!res.ok) throw new Error();
    const data = await res.json();
    const map: Record<string, unknown> = {};
    (data.results ?? []).forEach((s: { id: string; key?: string }) => { map[s.id] = s; if (s.key) map[s.key] = s; });
    return map;
  } catch (error) {
    console.error('[tax] Failed to fetch stores:', error);
    return {};
  }
};

// ─── Build Exact Tax Request ──────────────────────────────────────────

export const buildExactTaxRequest = (
  cart: Cart,
  billingAddress: AddressInput = {},
  shippingAddress: AddressInput = {},
  productDescriptions: Record<string, string> = {},
  userProfileId = '',
  categories: Record<string, unknown> = {},
  stores: Record<string, unknown> = {}
) => {
  const toLine1 = (a: AddressInput) => `${(a.streetName || a.street || '').trim()} ${(a.houseNumber || a.streetNumber || '').trim()}`.trim();
  const getAttr = (item: CartLineItem, name: string) => item.variant?.attributes?.find((a) => a.name === name)?.value ?? null;
  const getName = (item: CartLineItem) => (typeof item.name === 'object' ? item.name?.['en'] || Object.values(item.name ?? {})[0] : item.name) || DEFAULT_PRODUCT_NAME;

  const dateStr = new Date().toISOString().slice(0, 10);

  const makeAddress = (addr: AddressInput, type: string) => ({
    line1: toLine1(addr), line2: addr.line2 || '', city: addr.city || '',
    county: addr.county || '', province: addr.province || '', state: addr.state || '',
    country: addr.country || '', postalCode: addr.postalCode || '', type,
    customFields: [
      { name: TAX_ADDRESS_FIELDS.COUNTRY_NAME, value: addr.countryName || '' },
      { name: TAX_ADDRESS_FIELDS.STATE_CODE, value: '' },
      { name: TAX_ADDRESS_FIELDS.TAX_NUMBER, value: addr.taxNumber || '' },
      { name: TAX_ADDRESS_FIELDS.USER_PROFILE_ID, value: userProfileId },
    ],
  });

  const storeId = cart?.store?.id ?? '';
  const storeKey = cart?.store?.key ?? '';
  const store = (stores[storeId] || stores[storeKey] || stores[import.meta.env.VITE_STORE_ID ?? '']) as { custom?: { fields?: Record<string, string> } } | undefined;
  const sf = store?.custom?.fields ?? {};

  const legalCountry = sf.legalEntityCountryCode || '';
  const shipFrom = {
    street: ' ', city: ' ', county: ' ', postalCode: sf.legalEntityPostalCode || '',
    province: legalCountry === TAX_COUNTRIES.CANADA ? (sf.legalEntityStateProvince || '') : '',
    state: legalCountry === TAX_COUNTRIES.USA ? (sf.legalEntityStateProvince || '') : '',
    country: legalCountry,
  };

  const catId = import.meta.env.VITE_CATEGORY_ID || '';
  const ef = (categories[catId] as { custom?: { fields?: Record<string, string> } } | undefined)?.custom?.fields ?? {};
  const eventCountry = ef['eventCountryCode'] || '';
  const eventLocation = {
    street: ' ', city: ' ', county: ' ', postalCode: ef['eventPostalCode'] || '',
    province: eventCountry === TAX_COUNTRIES.CANADA ? (ef['eventStateProvince'] || '') : '',
    state: eventCountry === TAX_COUNTRIES.USA ? (ef['eventStateProvince'] || '') : '',
    country: eventCountry,
  };

  const baseId = cart?.id || Date.now().toString();

  return {
    meta: { requestId: baseId, primaryLanguage: DEFAULT_PRIMARY_LANGUAGE },
    data: {
      orderId: baseId,
      orderDate: dateStr,
      account: { taxNumber: billingAddress?.taxNumber ?? null },
      customer: { address: [makeAddress(billingAddress, 'billing'), makeAddress(shippingAddress, 'shipping')] },
      currency: cart?.totalPrice?.currencyCode || cart?.currency || DEFAULT_CURRENCY,
      shipFrom,
      eventLocation,
      tax: { isExempt: { all: false } },
      customAttributes: [{ name: null, value: null }],
      lineItems: (cart.lineItems ?? []).filter(Boolean).map((item, idx) => ({
        lineReference: idx + 1,
        transactionType: getAttr(item, TAX_ATTRIBUTES.TRANSACTION_TYPE),
        pointOfTitleTransfer: getAttr(item, TAX_ATTRIBUTES.TITLE_TRANSFER) || DEFAULT_TITLE_TRANSFER,
        priceData: {
          unitAmount: Math.round((item.price?.value?.centAmount ?? 0) / 100),
          productData: {
            name: getName(item),
            description: productDescriptions[item.productId],
            categoryCode: getAttr(item, TAX_ATTRIBUTES.PRODUCT_TAX_CATEGORY),
          },
        },
        customAttributes: [{ name: null, value: null }],
      })),
    },
  };
};
